import { DynamicTranslate } from '../constants/constants';
import { CargoType, Load, LocationType, OrganizationLocation, Stop } from '../types';

export const listToGroup = <T>(list: T[], keySelector: (item: T) => string) => {
    if (!Array.isArray(list)) {
        return {};
    }
    return list.reduce((acc: Record<string, T[]>, curr) => {
        const key = keySelector(curr);

        if (Object.prototype.hasOwnProperty.call(acc, key)) {
            acc[key].push(curr);
        } else {
            acc[key] = [curr];
        }
        return acc;
    }, {});
};

// SiteDescription (SiteName) - SublocationName
export const toSiteDisplayName = (siteName: string, siteDescription: string | null, sublocationNames?: string | null) => {
    const description = siteDescription ? siteDescription : '';
    return (`${description} (${siteName})${sublocationNames ? ` - ${sublocationNames}` : ''}`).trim();
};

export const toSiteDisplayNameFromOrganizationLocation = (organizationLocation: OrganizationLocation) => {
    return toSiteDisplayName(organizationLocation.name, organizationLocation.description);
}

// copy of version from use-location-info
export const toSublocationDisplay = (stopsWithSublocations: Stop[], organizationLocation: OrganizationLocation) => {
    if (!stopsWithSublocations.length) {
        return null;
    }

    const uniqueSublocationIds = [...new Set(stopsWithSublocations.flatMap(x => x.organizationSubLocationIds))];
    const sublocations = organizationLocation.orgSubLocations.filter(sub => uniqueSublocationIds.includes(sub.id)).sortBy(s => s.name) || [];
    return sublocations.map(s => s.name).join(' - ');
}

/**
 * 
 * @param stops - List of stops to convert to pairs of organization location IDs.
 * @param sort - Whether to sort the stops by their sequence before processing. Will be false if stops will be processed across multiple loads.
 * @default true
 * @returns 
 */
export const stopsToOrganizationLocationIdPairs = (stops: Stop[], sort: boolean = true) => {
    const referenceStops = sort ? stops.sortBy(stop => stop.sequence) : stops;
    const organizationLocationIdsList = referenceStops.map(stop => stop.organizationLocationId || 0) || [];

    const pairList: number[][] = [];

    for (let i = 0; i < organizationLocationIdsList.length - 1; i++) {
        if (organizationLocationIdsList[i] && organizationLocationIdsList[i + 1] && organizationLocationIdsList[i] > 0 && organizationLocationIdsList[i + 1] > 0) {
            pairList.push([organizationLocationIdsList[i], organizationLocationIdsList[i + 1]]);
        }
    }

    return pairList;
};

export const loadsToOrganizationLocationIdPairs = (loads: Load[]) => {
    const arrayOfOrganizationLocationIds = loads.map(load => {
        const sortedStops = load.stops?.sortBy(stop => stop.sequence);
        const orgLocationIds = sortedStops.map(sortedStop => sortedStop.organizationLocationId || 0);
        return orgLocationIds;
    }) || [];

    const pairList: number[][] = [];

    arrayOfOrganizationLocationIds.forEach(organizationLocationIds => {
        for (let i = 0; i < organizationLocationIds.length - 1; i++) {
            pairList.push([organizationLocationIds[i], organizationLocationIds[i + 1]]);
        }
    });

    return pairList;
};

export const orgSubLocationToItems = (locations: OrganizationLocation[], organizationLocationId: number | null | undefined) => {
    const currentLoc = locations.find(l => l.id === organizationLocationId);
    return currentLoc ? currentLoc.orgSubLocations.map(x => ({ id: x.id, value: x.name, })) : [];
}

export const cargoTypesToCategoryGroups = (cargoTypes: CargoType[]) => {
    return listToGroup(cargoTypes, (cargoType: CargoType) => DynamicTranslate(`cargoCategory${cargoType.category}`));
};

interface LocationTypeCategoriesMap {
    [key: string]: LocationType[]; // Key is the location category, value is LocationTypes of that category
}

export const locationTypesToCategoryGroups = (locationTypes: LocationType[]) => {
    return locationTypes.reduce((acc: LocationTypeCategoriesMap, locationType) => {
        if (locationType.cargoTypeCategories?.length) {
            return locationType.cargoTypeCategories.reduce((acc2, cargoType) => {
                const key = DynamicTranslate(`cargoCategory${cargoType}`);

                if (acc2[key]) {
                    acc2[key].push(locationType);
                } else {
                    acc2[key] = [locationType];
                }

                return acc2;
            }, acc);
        } else {
            acc[DynamicTranslate(`locationTypeValue${locationType.id}`)] = [locationType];
            return acc;
        }
    }, {});
};